import React, { useState, useEffect } from 'react';
import { generateCase, convertCaseStructure, improveCase } from '../../services/geminiService';
import { CaseData } from '../../types';
import { Sparkles, Save, Trash, PlusCircle, Search as SearchIcon, ChevronLeft, ChevronRight, Calendar, Play, Wand2, BookOpen, RefreshCw, ArrowRight, Shield, Maximize2, Siren, FileText } from 'lucide-react';
import { db, storage } from '../../services/firebase';
import { collection, doc, setDoc, getDoc, deleteDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const CaseManager: React.FC = () => {
    const [cases, setCases] = useState<CaseData[]>([]);
    const [generating, setGenerating] = useState(false);
    const [selectedCase, setSelectedCase] = useState<CaseData | null>(null);
    const [uploading, setUploading] = useState<{ hero: boolean, thumb: boolean }>({ hero: false, thumb: false });
    const [converting, setConverting] = useState(false);

    // Configurações de Geração IA
    const [genDifficulty, setGenDifficulty] = useState<'Fácil' | 'Médio' | 'Difícil'>('Médio');
    const [genTime, setGenTime] = useState<'Curto' | 'Médio' | 'Longo'>('Médio');
    const [genSeason, setGenSeason] = useState(false);

    // AI Themes & Seeds
    const [exclusiveSeed, setExclusiveSeed] = useState<string>('');
    const [aiThemesFromDB, setAiThemesFromDB] = useState<string[]>([]);
    const [availableCategories, setAvailableCategories] = useState<string[]>([]);

    // List Filters & Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;

    // Melhorar Caso
    const [improvementPrompt, setImprovementPrompt] = useState('');
    const [improving, setImproving] = useState(false);

    // Fetch general themes for generation pool when exclusive isn't used
    useEffect(() => {
        if (!db) return;
        getDoc(doc(db, 'settings', 'ai_prompts')).then(snap => {
            if (snap.exists() && snap.data().themes) {
                setAiThemesFromDB(snap.data().themes);
            }
        });

        getDoc(doc(db, 'settings', 'gameplay')).then(snap => {
            if (snap.exists() && snap.data().categories) {
                setAvailableCategories(snap.data().categories);
            } else {
                setAvailableCategories(['Casos Recentes', 'Mistérios Longos', 'Casos Sobrenaturais']);
            }
        });
    }, []);

    // Fetch cases from Firestore real-time
    useEffect(() => {
        if (!db) return;
        const q = query(collection(db, 'cases'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const loadedCases: CaseData[] = [];
            snapshot.forEach(doc => {
                loadedCases.push(doc.data() as CaseData);
            });
            setCases(loadedCases);
            // Verify if selectedCase was updated remotely
            setSelectedCase(prev => {
                if (!prev) return null;
                const updated = loadedCases.find(c => c.id === prev.id);
                return updated || null;
            });
        });
        return () => unsubscribe();
    }, []);

    const handleGenerate = async () => {
        if (!db) return alert("Banco não conectado.");
        setGenerating(true);
        try {
            // Se tiver uma semente exclusiva, usa só ela no array, anulando os padrões. Se estiver vazia, usa as do DB ou Default.
            const paramsThemes = exclusiveSeed.trim() !== '' ? [exclusiveSeed.trim()] : aiThemesFromDB;
            const newCase = await generateCase(genDifficulty, genTime, genSeason, paramsThemes);
            const newDocRef = doc(collection(db, 'cases'));
            newCase.id = newDocRef.id;
            newCase.status = 'active'; // Default player state
            newCase.publishStatus = 'draft';
            newCase.createdAt = Date.now();

            await setDoc(newDocRef, newCase);
            setSelectedCase(newCase);
            alert('Rascunho gerado e salvo com sucesso!');
        } catch (error: any) {
            console.error(error);
            const msg = error?.message || '';
            if (msg.includes('429') || msg.includes('Too Many Requests')) {
                alert('Limite da API do Gemini excedido (Erro 429). Tente novamente mais tarde ou crie um caso manualmente.');
            } else {
                alert('Falha ao gerar caso via IA: ' + msg);
            }
        } finally {
            setGenerating(false);
        }
    };

    const handleCreateBlank = async () => {
        if (!db) return alert("Banco não conectado.");
        try {
            const newDocRef = doc(collection(db, 'cases'));
            const newCase: CaseData = {
                id: newDocRef.id,
                title: "Novo Caso em Branco",
                description: "Descrição do caso...",
                solution: { culpritId: "char1", motive: "Motivo..." },
                locations: [{ id: "loc1", name: "Cena do Crime", description: "O local...", openTime: "00:00", closeTime: "23:59", x: 50, y: 50, type: "public", evidence: "Nenhuma evidência inicial." }],
                characters: [{ id: "char1", name: "Suspeito 1", role: "suspect", description: "Suspeito...", locationId: "loc1", phoneStatus: "available" }],
                createdAt: Date.now(),
                status: 'active',
                publishStatus: 'draft',
                lastSavedTime: 0,
                currentLocationId: 'HQ',
                scoutedLocationIds: [],
                visitedLocationIds: [],
                metCharacterIds: [],
                discoveredLocationIds: [],
                logs: [],
                knownCharacterNames: {},
                deadline: Date.now() + 86400000,
                inventory: [],
                userNotes: "",
                suspectAnnotations: {}
            };
            await setDoc(newDocRef, newCase);
            setSelectedCase(newCase);
            alert('Caso em branco criado com sucesso!');
        } catch (err) {
            console.error(err);
            alert("Erro ao criar caso em branco.");
        }
    };

    const handleConvert = async (c: CaseData) => {
        if (!window.confirm("Isso analisará a 'evidence' estática e a transformará numa árvore 'clues'. O caso precisará ser Salvo manualmente depois. Deseja continuar?")) return;
        setConverting(true);
        try {
            const updatedCase = await convertCaseStructure(c);
            setSelectedCase(updatedCase);
            alert("Caso convertido com sucesso! Verifique o JSON e clique em 'Salvar DB' para efetivar.");
        } catch (error) {
            console.error(error);
            alert("Falha ao converter caso. Verifique os logs.");
        } finally {
            setConverting(false);
        }
    };

    const handleSave = async (c: CaseData) => {
        if (!db) return;
        try {
            await setDoc(doc(db, 'cases', c.id), c);
            alert('Caso salvo e sincronizado!');
        } catch (error) {
            console.error("Save error: ", error);
            alert("Erro ao salvar o caso.");
        }
    };

    const handleImprove = async () => {
        if (!selectedCase || !improvementPrompt.trim()) return alert("Selecione um caso e digite o que deseja melhorar.");
        setImproving(true);
        try {
            const improved = await improveCase(selectedCase, improvementPrompt);
            setSelectedCase(improved);
            setImprovementPrompt('');
            alert("Caso melhorado pelo Arquiteto! Verifique o JSON abaixo e não esqueça de Salvar no DB.");
        } catch (error: any) {
            console.error(error);
            alert("Erro ao melhorar caso: " + (error?.message || "Erro desconhecido"));
        } finally {
            setImproving(false);
        }
    };

    const handleTest = () => {
        if (!selectedCase) return;
        localStorage.setItem('chronos_test_case_data', JSON.stringify(selectedCase));
        window.open('/game?test=true', '_blank');
    };

    const handleDelete = async (caseId: string) => {
        if (!db) return;
        if (window.confirm("Certeza que deseja deletar este caso permanente?")) {
            await deleteDoc(doc(db, 'cases', caseId));
            if (selectedCase?.id === caseId) setSelectedCase(null);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'hero' | 'thumb') => {
        if (!e.target.files || e.target.files.length === 0 || !selectedCase || !storage) return;
        const file = e.target.files[0];

        setUploading(prev => ({ ...prev, [type]: true }));
        try {
            const timestamp = Date.now();
            const storageRef = ref(storage, `cases/${selectedCase.id}/${type}_${timestamp}_${file.name}`);
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed',
                (snapshot) => {
                    // Progress could be updated here if needed
                },
                (error) => {
                    console.error("Upload error", error);
                    alert("Erro ao fazer upload da imagem.");
                    setUploading(prev => ({ ...prev, [type]: false }));
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    const updatedCase = { ...selectedCase };
                    if (type === 'hero') updatedCase.heroImageUrl = downloadURL;
                    if (type === 'thumb') updatedCase.thumbImageUrl = downloadURL;

                    setSelectedCase(updatedCase);
                    await setDoc(doc(db!, 'cases', updatedCase.id), updatedCase);
                    setUploading(prev => ({ ...prev, [type]: false }));
                }
            );
        } catch (error) {
            console.error(error);
            setUploading(prev => ({ ...prev, [type]: false }));
        }
    };

    const filteredCases = cases.filter(c => {
        const matchStatus = statusFilter === 'all' || c.publishStatus === statusFilter;
        const matchSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.description.toLowerCase().includes(searchTerm.toLowerCase());
        return matchStatus && matchSearch;
    });

    const totalPages = Math.ceil(filteredCases.length / ITEMS_PER_PAGE) || 1;
    const paginatedCases = filteredCases.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    return (
        <div className="p-6 bg-slate-50 min-h-screen">
            {/* Header / Titulo */}
            <div className="flex justify-between items-end mb-8">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">Gerenciamento de Casos</h1>
                    <p className="text-slate-500 text-sm font-medium">Arquitetura de mistérios e controle de missões ACME</p>
                </div>
            </div>
            {/* Command Center: Geração e Ações Rápidas */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-10">
                <div className="xl:col-span-3 bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 p-6 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full -mr-32 -mt-32 blur-3xl group-hover:bg-amber-500/10 transition-colors"></div>
                    
                    <div className="relative z-10">
                        <div className="flex flex-col md:flex-row gap-6">
                            {/* Generation Settings */}
                            <div className="flex-1">
                                <h3 className="text-amber-500 text-[10px] font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                    <Sparkles size={12} /> Parâmetros de Arquitetura IA
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Dificuldade</label>
                                        <select value={genDifficulty} onChange={e => setGenDifficulty(e.target.value as any)} className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-xs text-white outline-none focus:border-amber-500/50 transition-colors cursor-pointer">
                                            <option value="Fácil">Fácil</option>
                                            <option value="Médio">Médio</option>
                                            <option value="Difícil">Difícil</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Extensão</label>
                                        <select value={genTime} onChange={e => setGenTime(e.target.value as any)} className="w-full bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-xs text-white outline-none focus:border-amber-500/50 transition-colors cursor-pointer">
                                            <option value="Curto">Curto (~10m)</option>
                                            <option value="Médio">Médio (~30m)</option>
                                            <option value="Longo">Longo (+1h)</option>
                                        </select>
                                    </div>
                                    <div className="flex items-end pb-1.5">
                                        <label className="flex items-center gap-3 cursor-pointer group/check">
                                            <div className="relative">
                                                <input type="checkbox" checked={genSeason} onChange={e => setGenSeason(e.target.checked)} className="peer sr-only" />
                                                <div className="w-10 h-5 bg-slate-700 rounded-full peer-checked:bg-amber-600 transition-colors"></div>
                                                <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                                            </div>
                                            <span className="text-[10px] text-slate-300 font-bold uppercase tracking-wide group-hover/check:text-white transition-colors">Épico de Temporada</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="mt-6 flex flex-wrap gap-3">
                                    <button
                                        onClick={handleGenerate}
                                        disabled={generating}
                                        className="bg-amber-600 hover:bg-amber-500 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 shadow-[0_0_20px_rgba(217,119,6,0.2)] transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {generating ? <RefreshCw className="animate-spin w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                                        {generating ? 'Arquitetando...' : 'GERAR VIA IA'}
                                    </button>
                                    <button
                                        onClick={handleCreateBlank}
                                        disabled={generating}
                                        className="bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"
                                    >
                                        <PlusCircle className="w-4 h-4" />
                                        Manual
                                    </button>
                                </div>
                            </div>

                            {/* Seed Area */}
                            <div className="md:w-1/3 flex flex-col">
                                <h3 className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                    <BookOpen size={12} /> Contexto de Inspiração
                                </h3>
                                <textarea
                                    className="flex-1 w-full bg-slate-800/50 border border-slate-700 rounded-xl p-3 text-xs text-white focus:ring-1 focus:ring-amber-500/30 outline-none resize-none placeholder:text-slate-600"
                                    rows={3}
                                    value={exclusiveSeed}
                                    onChange={e => setExclusiveSeed(e.target.value)}
                                    placeholder="Ex: Homicídio em um clube de jazz de luxo..."
                                />
                                <p className="text-[9px] text-slate-500 mt-2 italic leading-tight">* Deixe vazio para temas aleatórios da agência.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Info / Tips */}
                <div className="bg-amber-500/5 border border-amber-500/10 rounded-2xl p-6 flex flex-col justify-between">
                    <div>
                        <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center mb-4 text-amber-500">
                            <BookOpen size={20} />
                        </div>
                        <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-2">Tutorial Arquiteto</h4>
                        <p className="text-[11px] text-slate-600 leading-relaxed">
                            A estrutura de casos agora suporta **Grafos de Pistas**. Garanta que os locais sejam desbloqueados via inspeção de itens ou diálogos.
                        </p>
                    </div>
                    <a 
                        href="file:///C:/Users/marko/.gemini/antigravity/brain/d9cc1091-4f70-4f15-ba66-d6d3066994d7/case_creation_guide.md" 
                        target="_blank" 
                        className="mt-4 flex items-center justify-between text-[10px] font-bold text-amber-700 uppercase bg-amber-500/10 hover:bg-amber-500/20 p-2.5 rounded-lg transition-colors group"
                    >
                        Ver Documentação <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Cases List Sidebar */}
                <div className="lg:col-span-1 flex flex-col h-[750px] space-y-4">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col flex-1">
                        <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex justify-between items-center mb-4">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Explorar Arquivos</span>
                                <div className="bg-slate-200 text-slate-600 text-[9px] font-bold px-2 py-0.5 rounded-full">{filteredCases.length}</div>
                            </div>
                            <div className="space-y-2">
                                <div className="relative">
                                    <SearchIcon size={14} className="absolute left-3 top-2.5 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar caso..."
                                        className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-3 py-2 text-xs outline-none focus:border-amber-500/50 transition-colors"
                                        value={searchTerm}
                                        onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>
                                <select
                                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-amber-500/50 transition-colors cursor-pointer text-slate-600"
                                    value={statusFilter}
                                    onChange={e => { setStatusFilter(e.target.value as any); setCurrentPage(1); }}
                                >
                                    <option value="all">Todos os Status</option>
                                    <option value="published">Somente Públicos</option>
                                    <option value="draft">Somente Rascunhos</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto divide-y divide-slate-100 scrollbar-thin scrollbar-thumb-slate-200">
                            {paginatedCases.length === 0 ? (
                                <div className="p-10 text-center flex flex-col items-center gap-3">
                                    <SearchIcon size={32} className="text-slate-200" />
                                    <p className="text-xs text-slate-400 font-medium">Nenhum dossiê encontrado.</p>
                                </div>
                            ) : (
                                paginatedCases.map(c => (
                                    <div
                                        key={c.id}
                                        onClick={() => setSelectedCase(c)}
                                        className={`p-4 cursor-pointer transition-all border-l-4 ${selectedCase?.id === c.id ? 'bg-amber-50/50 border-amber-500' : 'hover:bg-slate-50 border-transparent'}`}
                                    >
                                        <div className="flex justify-between items-start gap-3 mb-1.5">
                                            <h3 className={`font-bold text-xs line-clamp-2 leading-tight ${selectedCase?.id === c.id ? 'text-amber-900' : 'text-slate-700'}`}>{c.title}</h3>
                                            {c.publishStatus === 'published' ? (
                                                <div className="bg-emerald-100 text-emerald-700 text-[8px] font-black px-1.5 py-0.5 rounded uppercase shrink-0">Público</div>
                                            ) : (
                                                <div className="bg-slate-100 text-slate-500 text-[8px] font-black px-1.5 py-0.5 rounded uppercase shrink-0">Rascunho</div>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-slate-400 line-clamp-2 leading-relaxed mb-3">{c.description}</p>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <div className="bg-white border border-slate-100 text-slate-500 text-[8px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
                                                <Calendar size={8} /> {c.scheduledDate ? new Date(c.scheduledDate).toLocaleDateString() : 'SD'}
                                            </div>
                                            {c.isExtra && <div className="bg-purple-50 text-purple-600 text-[8px] font-black px-1.5 py-0.5 rounded uppercase border border-purple-100">EXTRA</div>}
                                            {c.category && <div className="bg-slate-100 text-slate-600 text-[8px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap overflow-hidden max-w-[80px] text-ellipsis">{c.category}</div>}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {totalPages > 1 && (
                            <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-between items-center bg-slate-50/50">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 hover:bg-white rounded-lg text-slate-400 disabled:opacity-30 transition-colors"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="text-[10px] font-black text-slate-400 tabular-nums">
                                    {currentPage} <span className="mx-1 text-slate-300">/</span> {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 hover:bg-white rounded-lg text-slate-400 disabled:opacity-30 transition-colors"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>


                {/* Editor Area */}
                <div className="lg:col-span-3 flex flex-col h-[750px]">
                    {!selectedCase ? (
                        <div className="flex-1 bg-slate-100/50 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 space-y-4">
                            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-slate-200 flex items-center justify-center opacity-60">
                                <Calendar size={32} />
                            </div>
                            <div className="text-center">
                                <p className="font-bold text-sm">Nenhum Caso Selecionado</p>
                                <p className="text-xs">Selecione um arquivo lateral para editar ou gere um novo via IA no topo.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col">
                            {/* Editor Header */}
                            <div className="px-6 py-4 bg-slate-900 flex justify-between items-center text-white shrink-0">
                                <div className="flex items-center gap-4">
                                    <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center text-slate-900">
                                        <Save size={18} />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-black uppercase tracking-widest leading-none mb-1">{selectedCase.title || 'Novo Caso'}</h2>
                                        <p className="text-[10px] text-slate-400 font-bold font-mono">ID: {selectedCase.id}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button 
                                        onClick={() => handleSave(selectedCase)} 
                                        className="bg-amber-600 hover:bg-amber-500 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-colors"
                                    >
                                        <Save size={14} /> Salvar DB
                                    </button>
                                    <button 
                                        onClick={handleTest} 
                                        className="bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-colors"
                                    >
                                        <Play size={14} /> Testar
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(selectedCase.id)} 
                                        className="bg-slate-800 hover:bg-red-900/40 text-slate-400 hover:text-red-400 p-2 rounded-lg transition-all"
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Main Scrollable Content Area */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-thin scrollbar-thumb-slate-200">
                                
                                {/* AI POWER TOOLS SECTION */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 shadow-sm">
                                        <label className="block text-[10px] font-black text-amber-700 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                            <Wand2 size={12} /> Refinamento Assistido (IA Arquiteta)
                                        </label>
                                        <div className="flex flex-col gap-3">
                                            <textarea
                                                value={improvementPrompt}
                                                onChange={(e) => setImprovementPrompt(e.target.value)}
                                                placeholder="Ex: 'Torne o motivo mais obscuro', 'Adicione uma pista no bueiro'..."
                                                className="w-full bg-white border border-amber-200 rounded-xl p-3 text-xs text-slate-800 focus:ring-1 focus:ring-amber-500 outline-none h-20 resize-none shadow-inner"
                                            />
                                            <button
                                                onClick={handleImprove}
                                                disabled={improving || !improvementPrompt.trim()}
                                                className="w-full py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-md active:scale-[0.98]"
                                            >
                                                {improving ? <RefreshCw className="animate-spin w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                                                {improving ? 'Refinando...' : 'Aplicar Melhoria IA'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col justify-between">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                                                <RefreshCw size={12} /> Conversão de Legado
                                            </label>
                                            <p className="text-[10px] text-slate-500 leading-relaxed mb-4">
                                                Caso o dossiê utilize o formato antigo (sem pistas encadeadas), o Arquiteto pode reestruturá-lo mantendo a narrativa original.
                                            </p>
                                        </div>
                                        {selectedCase.locations?.[0] && !selectedCase.locations[0].clues && (
                                            <button
                                                onClick={() => handleConvert(selectedCase)}
                                                disabled={converting}
                                                className="w-full py-3 bg-white border border-slate-300 hover:bg-slate-900 hover:text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                                            >
                                                <RefreshCw className={converting ? "animate-spin w-3 h-3" : "w-3 h-3"} />
                                                {converting ? 'Adaptando...' : 'Adaptar Pistas para 2.0'}
                                            </button>
                                        )}
                                        {selectedCase.locations?.[0]?.clues && (
                                          <div className="text-center py-3 text-emerald-600 font-bold text-[10px] uppercase tracking-widest flex items-center justify-center gap-2">
                                            <Shield size={12} /> Dossiê Já Estruturado (2.0)
                                          </div>
                                        )}
                                    </div>
                                </div>

                                {/* CASE METADATA SECTION */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-4">
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Shield size={12} /> Visibilidade & Publicação</h4>
                                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-4">
                                            <div>
                                                <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5 px-1">Status</label>
                                                <select
                                                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-bold text-slate-700 outline-none focus:border-amber-500/50"
                                                    value={selectedCase.publishStatus || 'draft'}
                                                    onChange={(e) => setSelectedCase({ ...selectedCase, publishStatus: e.target.value as 'draft' | 'published' })}
                                                >
                                                    <option value="draft">📁 Rascunho Privado</option>
                                                    <option value="published">👁️ Publicado (Ao Vivo)</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5 px-1">Seção da Home</label>
                                                <select
                                                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-bold text-slate-700 outline-none focus:border-amber-500/50"
                                                    value={selectedCase.category || ''}
                                                    onChange={(e) => setSelectedCase({ ...selectedCase, category: e.target.value })}
                                                >
                                                    <option value="">🚫 Oculto da Home</option>
                                                    {availableCategories.map(cat => (
                                                        <option key={cat} value={cat}>{cat}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Calendar size={12} /> Agenda & Extras</h4>
                                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-4">
                                            <div>
                                                <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5 px-1">Data de Lançamento</label>
                                                <input
                                                    type="date"
                                                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-bold text-slate-700 outline-none focus:border-amber-500/50"
                                                    value={selectedCase.scheduledDate ? new Date(selectedCase.scheduledDate).toISOString().split('T')[0] : ''}
                                                    onChange={(e) => {
                                                        const date = new Date(e.target.value + 'T12:00:00');
                                                        setSelectedCase({ ...selectedCase, scheduledDate: e.target.value ? date.getTime() : undefined });
                                                    }}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-2 pt-1">
                                                <label className="flex items-center justify-between p-2 bg-white border border-slate-100 rounded-lg cursor-pointer group hover:border-amber-500/30 transition-all">
                                                    <span className="text-[10px] font-bold text-slate-600 uppercase">Dossiê Extra</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCase.isExtra || false}
                                                        onChange={(e) => setSelectedCase({ ...selectedCase, isExtra: e.target.checked })}
                                                        className="w-4 h-4 rounded text-amber-600 border-slate-300 focus:ring-amber-500"
                                                    />
                                                </label>
                                                <label className="flex items-center justify-between p-2 bg-white border border-slate-100 rounded-lg cursor-pointer group hover:border-amber-500/30 transition-all">
                                                    <span className="text-[10px] font-bold text-slate-600 uppercase">Temporada</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCase.isSpecialSeason || false}
                                                        onChange={(e) => setSelectedCase({ ...selectedCase, isSpecialSeason: e.target.checked })}
                                                        className="w-4 h-4 rounded text-amber-600 border-slate-300 focus:ring-amber-500"
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Maximize2 size={12} /> Atributos Técnicos</h4>
                                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-4">
                                            <div>
                                                <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5 px-1">Nível de Dificuldade</label>
                                                <select
                                                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-bold text-slate-700 outline-none focus:border-amber-500/50"
                                                    value={selectedCase.difficulty || 'Médio'}
                                                    onChange={(e) => setSelectedCase({ ...selectedCase, difficulty: e.target.value as any })}
                                                >
                                                    <option value="Fácil">🟢 Fácil</option>
                                                    <option value="Médio">🟡 Médio</option>
                                                    <option value="Difícil">🔴 Difícil</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5 px-1">Tempo Estimado</label>
                                                <select
                                                    className="w-full bg-white border border-slate-200 rounded-lg p-2 text-xs font-bold text-slate-700 outline-none focus:border-amber-500/50"
                                                    value={selectedCase.estimatedTime || 'Médio'}
                                                    onChange={(e) => setSelectedCase({ ...selectedCase, estimatedTime: e.target.value as any })}
                                                >
                                                    <option value="Curto">⚡ Curto (~10m)</option>
                                                    <option value="Médio">⚖️ Médio (~30m)</option>
                                                    <option value="Longo">🕵️ Longo (+1h)</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* IMAGES SECTION */}
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Siren size={12} /> Identidade Visual (Dossiê)</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 overflow-hidden group">
                                            <div className="mb-3 flex justify-between items-center px-1">
                                                <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Capa Principal (Hero)</label>
                                                <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">1920x1080px</span>
                                            </div>
                                            <div className="aspect-video bg-white rounded-xl border border-slate-200 overflow-hidden relative shadow-sm border-dashed">
                                                {selectedCase.heroImageUrl ? (
                                                  <div className="absolute inset-0 group/img">
                                                    <img src={selectedCase.heroImageUrl} alt="Hero" className="w-full h-full object-cover transition-transform group-hover/img:scale-105" />
                                                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                                                      <div className="bg-white text-slate-900 text-[10px] font-black py-2 px-4 rounded-lg shadow-xl cursor-pointer hover:bg-amber-500 transition-colors uppercase tracking-widest">Alterar Imagem</div>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 gap-2">
                                                    <PlusCircle size={24} />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">Selecionar Imagem</span>
                                                  </div>
                                                )}
                                                <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleImageUpload(e, 'hero')} disabled={uploading.hero} />
                                                {uploading.hero && <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center text-amber-500 text-[10px] font-black uppercase tracking-[0.2em] animate-pulse">Enviando Arquivo...</div>}
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 overflow-hidden group">
                                            <div className="mb-3 flex justify-between items-center px-1">
                                                <label className="text-[10px] font-black text-slate-700 uppercase tracking-widest">Capa Carrossel (Thumb)</label>
                                                <span className="text-[8px] text-slate-400 font-bold uppercase tracking-widest">600x400px</span>
                                            </div>
                                            <div className="aspect-[3/2] bg-white rounded-xl border border-slate-200 overflow-hidden relative shadow-sm border-dashed">
                                                {selectedCase.thumbImageUrl ? (
                                                  <div className="absolute inset-0 group/img">
                                                    <img src={selectedCase.thumbImageUrl} alt="Thumb" className="w-full h-full object-cover transition-transform group-hover/img:scale-105" />
                                                    <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                                                      <div className="bg-white text-slate-900 text-[10px] font-black py-2 px-4 rounded-lg shadow-xl cursor-pointer hover:bg-amber-500 transition-colors uppercase tracking-widest">Alterar Imagem</div>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 gap-2">
                                                    <PlusCircle size={24} />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">Selecionar Imagem</span>
                                                  </div>
                                                )}
                                                <input type="file" accept="image/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={(e) => handleImageUpload(e, 'thumb')} disabled={uploading.thumb} />
                                                {uploading.thumb && <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center text-amber-500 text-[10px] font-black uppercase tracking-[0.2em] animate-pulse">Enviando Arquivo...</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* JSON EDITOR SECTION */}
                                <div className="space-y-4 pb-12">
                                    <div className="flex justify-between items-center">
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><FileText size={12} /> Raw JSON Engine (Dados Grosseiros)</h4>
                                        <div className="flex items-center gap-2 text-[8px] font-black text-slate-400 uppercase bg-slate-100 px-2 py-1 rounded">
                                            <Shield size={10} className="text-amber-600" /> Cuidado: Erros de sintaxe impedem o salvamento.
                                        </div>
                                    </div>
                                    <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden shadow-2xl">
                                        <textarea
                                            className="w-full h-[500px] p-6 font-mono text-[11px] bg-slate-900 text-amber-500 selection:bg-amber-500 selection:text-slate-900 resize-none outline-none scrollbar-thin scrollbar-thumb-slate-700"
                                            value={typeof selectedCase === 'string' ? selectedCase : JSON.stringify(selectedCase, null, 2)}
                                            onChange={(e) => {
                                                try {
                                                    const parsed = JSON.parse(e.target.value);
                                                    setSelectedCase(parsed as CaseData);
                                                } catch (err) {
                                                    setSelectedCase(e.target.value as any);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default CaseManager;
